import { interpolate } from 'remotion';

// Constants
import { CLAMP_BOTH } from '@/constants';
import {
  ANIMATION_START_FRAME,
  CATEGORY_TRANSITION_GAP,
  EXIT_DURATION,
  FLIP_STAGGER_DELAY,
  FLIP_START_DELAY,
  HOLD_DURATION,
  MAX_CARD_DISPLAY,
  PHASE_1_MAX_CARDS,
  PHASE_FLIP_TO_HOLD_BUFFER,
  STAGGER_DELAY,
} from './constants';

// Types
import { EmployeeTraining, Mentee, Supportee } from '@/types/employee';
import { TeamMember } from './types';

interface SupporterAggregate {
  name: string;
  avatar: string;
  count: number;
  email: string;
  latestTopicLabel: string;
  latestStartDate: string;
}

/**
 * Extract the top 5 most frequent supporters from trainings data
 * and convert them to TeamMember[] format for display.
 * The badgeText shows the topic label of their most recent training.
 */
export const getTopSupportersFromTrainings = (
  trainings: EmployeeTraining[],
): TeamMember[] => {
  const supporterMap = new Map<string, SupporterAggregate>();

  trainings.forEach((training) => {
    training.supporters.forEach((supporter) => {
      const { name, avatar, email } = supporter;
      const { start, topicLabel } = training;

      if (!name) return;

      const existing = supporterMap.get(name);
      const startDate = start ?? '';

      if (existing) {
        existing.count += 1;

        if (avatar) {
          existing.avatar = avatar;
        }

        // Track the most recent training's topic label
        if (startDate >= existing.latestStartDate) {
          existing.latestTopicLabel = topicLabel;
          existing.latestStartDate = startDate;
        }
      } else {
        supporterMap.set(name, {
          name,
          avatar: avatar ?? '',
          count: 1,
          latestTopicLabel: topicLabel,
          latestStartDate: startDate,
          email: email ?? '',
        });
      }
    });
  });

  const sorted = Array.from(supporterMap.values()).sort(
    (a, b) => b.count - a.count,
  );

  const topSupporters = sorted.slice(0, MAX_CARD_DISPLAY);

  return topSupporters.map((supporter, index) => {
    const { name, avatar, email, latestTopicLabel } = supporter;
    const nameParts = name.split(' ');
    const firstName = (nameParts[0] ?? '').toUpperCase();
    const lastName = nameParts.slice(1).join(' ').toUpperCase() || '';

    return {
      id: email ?? index.toString(),
      firstName,
      lastName,
      badgeText: latestTopicLabel,
      imageUrl: avatar,
      rotation: -5,
    };
  });
};

/**
 * Convert mentees data from the schema into TeamMember[] format for display.
 */
export const convertMenteesToTeamMembers = (
  mentees: Mentee[],
): TeamMember[] => {
  return mentees.slice(0, MAX_CARD_DISPLAY).map((mentee, index) => {
    const nameParts = (mentee.name ?? '').split(' ');
    const firstName = (nameParts[0] ?? '').toUpperCase();
    const lastName = nameParts.slice(1).join(' ').toUpperCase() || '';

    return {
      id: mentee.email ?? index.toString(),
      firstName,
      lastName,
      imageUrl: mentee.avatar ?? '',
      rotation: -5,
    };
  });
};

/**
 * Convert trainees data from the schema into TeamMember[] format for display.
 * The badgeText shows the first training's topic label.
 */
export const convertSupporteesToTeamMembers = (
  supportees: Supportee[],
): TeamMember[] => {
  return supportees.slice(0, MAX_CARD_DISPLAY).map((supportee, index) => {
    const nameParts = (supportee.name ?? '').split(' ');
    const firstName = (nameParts[0] ?? '').toUpperCase();
    const lastName = nameParts.slice(1).join(' ').toUpperCase() || '';
    const badgeText = supportee.trainings?.[0]?.topicLabel ?? '';

    return {
      id: supportee.email ?? index.toString(),
      firstName,
      lastName,
      badgeText,
      imageUrl: supportee.avatar ?? '',
      rotation: -5,
    };
  });
};

/**
 * Calculate the total duration of a phase (slide-in + flip + hold)
 */
export const calculatePhaseDuration = (cardCount: number): number => {
  const lastSlideDelay =
    ANIMATION_START_FRAME + (cardCount - 1) * STAGGER_DELAY;
  const flipStart = lastSlideDelay + FLIP_START_DELAY;
  const lastFlipDelay = flipStart + (cardCount - 1) * FLIP_STAGGER_DELAY;

  return lastFlipDelay + PHASE_FLIP_TO_HOLD_BUFFER + HOLD_DURATION;
};

/**
 * Split members into phases: only when there are exactly 5 members,
 * first phase shows 3, second phase shows the rest (2).
 */
const splitIntoPhaseCounts = (memberCount: number): number[] => {
  if (memberCount !== 5) {
    return [memberCount];
  }

  return [PHASE_1_MAX_CARDS, memberCount - PHASE_1_MAX_CARDS];
};

/**
 * Calculate total duration for a single category including all its phases and exits
 */
export const calculateCategoryDurationFromCount = (
  memberCount: number,
): number => {
  const phaseCounts = splitIntoPhaseCounts(memberCount);

  if (phaseCounts.length === 1) {
    return calculatePhaseDuration(phaseCounts[0] ?? 0) + EXIT_DURATION;
  }

  const phase1Duration = calculatePhaseDuration(phaseCounts[0] ?? 0);
  const phase2Duration = calculatePhaseDuration(phaseCounts[1] ?? 0);

  return (
    phase1Duration +
    EXIT_DURATION +
    CATEGORY_TRANSITION_GAP +
    phase2Duration +
    EXIT_DURATION
  );
};

/**
 * Calculate the total duration of Scene 7 based on the number of members
 * in each category (supporters, supportees, mentees).
 *
 * @param categoryCounts - Array of member counts for each category, e.g. [4, 3, 2]
 * @returns Total duration in frames
 */
export const calculateTotalScene7Duration = (
  categoryCounts: number[],
): number => {
  const nonEmptyCategories = categoryCounts.filter((count) => count > 0);

  if (nonEmptyCategories.length === 0) return 0;

  let totalDuration = 0;

  nonEmptyCategories.forEach((count, index) => {
    totalDuration += calculateCategoryDurationFromCount(count);

    // Add gap between categories (except after the last one)
    if (index < nonEmptyCategories.length - 1) {
      totalDuration += CATEGORY_TRANSITION_GAP;
    }
  });

  return totalDuration;
};

/** Checks if a scene exit animation is currently active */
export const isSceneExitActive = (
  frame: number,
  exitStartFrame?: number,
  exitDuration?: number,
): boolean => {
  if (exitStartFrame == null || (exitDuration ?? 0) <= 0) return false;

  return frame >= exitStartFrame;
};

export const getSceneExitProgress = (
  frame: number,
  exitStartFrame?: number,
  exitDuration?: number,
): number => {
  if (!isSceneExitActive(frame, exitStartFrame, exitDuration)) return 0;

  return interpolate(
    frame - (exitStartFrame ?? 0),
    [0, exitDuration ?? 0],
    [0, 1],
    CLAMP_BOTH,
  );
};
